import { callFunction, callFunctionGet } from "./client";

export type BillingType = "PIX" | "BOLETO" | "CREDIT_CARD";
export type InvoiceStatus = "pending" | "paid" | "overdue" | "cancelled";
export type PaymentKind = "monthly_fee" | "donation";

export interface AssistedPaymentResponse {
  operation_id: string;
  protocol_number: string;
  status: "completed" | "processing" | "reconciliation_required";
  invoice_id?: string;
}

export interface CashReceiptInput {
  payment_id: string;
  operation_id: string;
  confirmation: "cash_received_in_full";
}

export interface CancelPaymentInput {
  payment_id: string;
  operation_id: string;
  justification: string;
}

export interface ReplacePaymentInput extends CancelPaymentInput {
  billing_type: "PIX" | "BOLETO";
  due_date?: string;
}

export function recordCashPayment(accessToken: string, input: CashReceiptInput): Promise<AssistedPaymentResponse> {
  return callFunction<CashReceiptInput, AssistedPaymentResponse>("payments-record-cash", input, { accessToken });
}

export function cancelPayment(accessToken: string, input: CancelPaymentInput): Promise<AssistedPaymentResponse> {
  return callFunction<CancelPaymentInput, AssistedPaymentResponse>("payments-cancel", input, { accessToken });
}

export function replacePayment(accessToken: string, input: ReplacePaymentInput): Promise<AssistedPaymentResponse> {
  return callFunction<ReplacePaymentInput, AssistedPaymentResponse>("payments-replace", input, { accessToken });
}

export interface FinancialInvoice {
  id: string;
  kind: PaymentKind;
  competencia: string | null;
  amount: string | number;
  due_date: string;
  status: InvoiceStatus;
  payment_method: "pix" | "boleto" | "credit_card" | null;
  paid_at: string | null;
}

export interface FinancialReceipt {
  id: string;
  invoice_id: string;
  receipt_reference: string;
  amount: string | number;
  received_at: string;
}

export interface FinancialOverview {
  member_id: string;
  invoices: FinancialInvoice[];
  receipts: FinancialReceipt[];
}

export type CreateMonthlyFeeInput = {
  kind: "monthly_fee";
  member_id?: string;
  competencia: string;
  billing_type: BillingType;
  due_date?: string;
  recurring?: boolean;
};

export type CreateDonationInput = {
  kind: "donation";
  amount: number;
  billing_type: BillingType;
};

export type CreatePaymentInput = CreateMonthlyFeeInput | CreateDonationInput;

export type CreatePaymentResponse =
  | { invoice_id: string; kind: "monthly_fee" | "donation"; amount: number; due_date: string; status: "pending" }
  | { subscription_id: string; kind: "monthly_fee"; amount: number; due_date: string; status: "active" };

export interface PaymentInstructions {
  invoice_id: string;
  kind: PaymentKind;
  status: InvoiceStatus;
  due_date: string;
  instructions: {
    payment_url: string | null;
    bank_slip_url: string | null;
    pix: {
      encoded_image: string | null;
      payload: string | null;
      expiration_date: string | null;
    } | null;
  };
}

export function getFinancialOverview(accessToken: string): Promise<FinancialOverview> {
  return callFunctionGet<FinancialOverview>("payments-list", {}, { accessToken });
}

export function getMemberFinancialOverview(accessToken: string, memberId: string): Promise<FinancialOverview> {
  return callFunctionGet<FinancialOverview>("member-financial-overview", { member_id: memberId }, { accessToken });
}

export function createPayment(accessToken: string, input: CreatePaymentInput): Promise<CreatePaymentResponse> {
  return callFunction<CreatePaymentInput, CreatePaymentResponse>("payments-create", input, { accessToken });
}

export function getPaymentInstructions(accessToken: string, invoiceId: string): Promise<PaymentInstructions> {
  return callFunction<{ invoice_id: string }, PaymentInstructions>(
    "payments-instructions",
    { invoice_id: invoiceId },
    { accessToken },
  );
}
